import { AlertTriangle, CheckCircle2, Download, FileSpreadsheet, LoaderCircle, UploadCloud } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, downloadFile, type MaintenanceImportResult } from "../api";
import { MetricStrip, PageHeader, Pagination, StatusBadge, TaskTypeIcon, readValue, typeLabels } from "../components";
import { useAppData } from "../context";
import type { ImportJob, Precheck, TaskType } from "../types";
import { ShippingPricingPanel } from "../ShippingPricingPanel";
import { OfficialPricingPanel } from "../OfficialPricingPanel";
import { OneTimePricingPanel } from "../OneTimePricingPanel";

interface TaskOption {
  id: string;
  label: string;
  iconType: TaskType;
  types: TaskType[];
}

const taskOptions: TaskOption[] = [
  { id: "CONTENT_UPDATE", label: "已有标题 / 描述", iconType: "TITLE_UPDATE", types: ["CONTENT_UPDATE"] },
  { id: "INVENTORY_UPDATE", label: "批量改库存", iconType: "INVENTORY_UPDATE", types: ["INVENTORY_UPDATE"] },
  { id: "NET_PROCEEDS_UPDATE", label: "批量调价（直发 / Full）", iconType: "NET_PROCEEDS_UPDATE", types: ["NET_PROCEEDS_UPDATE"] },
  { id: "LISTING_TYPE_UPDATE", label: "售价大于 15 升级 Premium", iconType: "LISTING_TYPE_UPDATE", types: ["LISTING_TYPE_UPDATE"] },
];
const taskTypes = taskOptions.flatMap((option) => option.types);

function expandTaskSelection(types: TaskType[]) {
  types = types.map(type => type === "TITLE_UPDATE" || type === "DESCRIPTION_UPDATE" ? "CONTENT_UPDATE" : type);
  return taskOptions.flatMap((option) => option.types.some((type) => types.includes(type)) ? option.types : []);
}

export function NewTask() {
  const [params] = useSearchParams();
  const initial = params.get("type") as TaskType | null;
  const normalizedInitial = initial === "TITLE_UPDATE" || initial === "DESCRIPTION_UPDATE" ? "CONTENT_UPDATE" : initial;
  const initialType = taskTypes.includes(normalizedInitial as TaskType) ? (normalizedInitial as TaskType) : "NET_PROCEEDS_UPDATE";
  const [selectedTypes, setSelectedTypes] = useState<TaskType[]>(() => expandTaskSelection([initialType]));
  const [note, setNote] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [prechecks, setPrechecks] = useState<Precheck[]>([]);
  const [activePrecheckType, setActivePrecheckType] = useState<TaskType>(initialType);
  const [importJob, setImportJob] = useState<ImportJob<MaintenanceImportResult> | null>(null);
  const [createdTasks, setCreatedTasks] = useState<Array<{ id: string; taskNo: string; status: string }>>([]);
  const [importJobId, setImportJobId] = useState(() => window.localStorage.getItem("meliforge-maintenance-import"));
  const [filter, setFilter] = useState("ALL");
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const submittingRef = useRef(false);
  const navigate = useNavigate();
  const { data, refresh } = useAppData();

  useEffect(() => {
    if (!importJobId) return;
    let stopped = false;
    async function poll() {
      try {
        const job = await api.importJob<MaintenanceImportResult>(importJobId!);
        if (stopped) return;
        setImportJob(job);
        if (job.status === "COMPLETED" && job.result) {
          if ("executionMode" in job.result) {
            setCreatedTasks(job.result.tasks);
            setPrechecks([]);
            void refresh();
          } else {
          const results = Array.isArray(job.result) ? job.result : [job.result];
          setPrechecks(results);
          setSelectedTypes(expandTaskSelection(results.map((result) => result.taskType)));
          setActivePrecheckType(results[0].taskType);
          setPage(1);
          }
          window.localStorage.removeItem("meliforge-maintenance-import");
          setImportJobId(null);
        } else if (job.status === "FAILED") {
          setError(job.errorMessage || "后台解析失败。");
          window.localStorage.removeItem("meliforge-maintenance-import");
          setImportJobId(null);
        }
      } catch (cause) {
        if (!stopped) setError(cause instanceof Error ? cause.message : "导入进度读取失败。");
      }
    }
    void poll();
    const timer = window.setInterval(() => void poll(), 900);
    return () => { stopped = true; window.clearInterval(timer); };
  }, [importJobId]);

  const activePrecheck = useMemo(
    () => prechecks.find((entry) => entry.taskType === activePrecheckType) ?? prechecks[0] ?? null,
    [activePrecheckType, prechecks],
  );
  const filtered = useMemo(
    () => activePrecheck?.details.filter((detail) => filter === "ALL" || detail.status === filter) ?? [],
    [activePrecheck, filter],
  );
  const submittablePrechecks = useMemo(
    () => prechecks.filter((entry) => entry.summary.executable > 0),
    [prechecks],
  );
  const pageSize = 50;
  const visible = filtered.slice((page - 1) * pageSize, page * pageSize);
  const totalExecutable = submittablePrechecks.reduce((total, entry) => total + entry.summary.executable, 0);
  const totalBlocked = prechecks.reduce((total, entry) => total + entry.summary.blocked, 0);
  const selectedOptions = taskOptions.filter((option) => option.types.every((type) => selectedTypes.includes(type)));

  async function runPrecheck(executionMode: "RUN" | "PREVIEW") {
    if (submittingRef.current || importJobId) return;
    if (!selectedTypes.length) {
      setError("请至少勾选一种批量功能。");
      return;
    }
    if (!file) {
      setError("请选择 CSV 或 XLSX 文件。");
      return;
    }
    submittingRef.current = true;
    setBusy("precheck");
    setError("");
    try {
      const job = await api.uploadPrechecks(selectedTypes, note, file, executionMode);
      setCreatedTasks([]);
      setPrechecks([]);
      setImportJob(job);
      setImportJobId(job.id);
      window.localStorage.setItem("meliforge-maintenance-import", job.id);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "预检失败。");
    } finally {
      submittingRef.current = false;
      setBusy("");
    }
  }

  async function submit() {
    if (!submittablePrechecks.length || submittingRef.current) return;
    submittingRef.current = true;
    setBusy("submit");
    setError("");
    setPage(1);
    try {
      let taskIds: string[];
      if (submittablePrechecks.length === 1) {
        const task = await api.submitPrecheck(submittablePrechecks[0].id);
        taskIds = [task.id];
      } else {
        const result = await api.submitPrechecks(submittablePrechecks.map((entry) => entry.id));
        taskIds = result.tasks.map((task) => task.id);
      }
      await refresh();
      navigate(taskIds.length === 1 ? `/tasks/${taskIds[0]}` : "/tasks");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "任务创建失败。");
    } finally {
      submittingRef.current = false;
      setBusy("");
    }
  }

  async function downloadTemplate() {
    if (!selectedTypes.length) {
      setError("请先勾选需要执行的批量功能。");
      return;
    }
    setError("");
    try {
      const selectedOption = selectedOptions.length === 1 ? selectedOptions[0] : undefined;
      if (selectedOption?.types.length === 1) {
        const type = selectedOption.types[0];
        await downloadFile(`/api/templates/${type}`, `meliforge-${type.toLowerCase()}-template.xlsx`);
      } else {
        const fileName = selectedOption?.id === "CONTENT_UPDATE" ? "meliforge-title-description-template.xlsx" : "meliforge-multi-task-template.xlsx";
        await downloadFile(`/api/templates/batch?taskTypes=${encodeURIComponent(selectedTypes.join(","))}`, fileName);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "模板下载失败。");
    }
  }

  function toggleOption(option: TaskOption) {
    setSelectedTypes((current) => {
      const selected = option.types.every((type) => current.includes(type));
      return taskTypes.filter((type) => selected ? !option.types.includes(type) && current.includes(type) : option.types.includes(type) || current.includes(type));
    });
    setPrechecks([]);
    setImportJob(null);
    setFilter("ALL");
    setPage(1);
    setError("");
  }

  function chooseFile(nextFile: File | null) {
    if (nextFile && !/\.(?:csv|xlsx)$/i.test(nextFile.name)) {
      setFile(null);
      setError("商品清单必须是 CSV 或 XLSX 文件。");
      return;
    }
    setFile(nextFile);
    setPrechecks([]);
    setImportJob(null);
    setFilter("ALL");
    setPage(1);
    setError("");
  }

  return <>
    <PageHeader eyebrow="BULK MAINTENANCE" title="批量商品维护" />
    {data && !data.writeEnabled ? <div className="risk-banner"><AlertTriangle size={20} /><div><strong>生产只读模式</strong><span>可以下载模板并运行文件预检；任务提交和所有商品写入均已锁定。</span></div></div> : null}
    <div className="step-strip" aria-label="任务步骤">
      {["选择维护方式", "上传文件并执行", "查看结果 / 下载"].map((label, index) => <div className={index <= (createdTasks.length || prechecks.length ? 2 : file ? 1 : 0) ? "active" : ""} key={label}><span>{index + 1}</span>{label}</div>)}
    </div>

    <section className="workspace-band new-task-panel">
      <div className="field-group">
        <div className="field-label-row">
          <label>选择批量功能</label>
          <span>{selectedOptions.length} 项已选择</span>
        </div>
        <div className="task-type-checklist">
          {taskOptions.map((option) => {
            const selected = option.types.every((type) => selectedTypes.includes(type));
            return <label className={`task-type-option ${selected ? "selected" : ""}`} key={option.id}>
              <input type="checkbox" checked={selected} disabled={Boolean(importJobId)} onChange={() => toggleOption(option)} />
              <TaskTypeIcon type={option.iconType} />
              <strong>{option.label}</strong>
            </label>;
          })}
        </div>
      </div>
      <div className="new-task-grid">
        <div className="field-group">
          <label htmlFor="task-note">任务说明</label>
          <input id="task-note" value={note} maxLength={120} onChange={(event) => setNote(event.target.value)} placeholder="例如：墨西哥站点 7 月收益校准" />
        </div>
        <div className="template-action">
          <button className="button secondary" disabled={!selectedOptions.length} onClick={() => void downloadTemplate()}><Download size={17} />{selectedOptions.length > 1 ? `下载多功能模板（${selectedOptions.length} 项）` : `下载${selectedOptions[0]?.label ?? ""}模板`}</button>
        </div>
      </div>
      <div className={`drop-zone ${file ? "has-file" : ""}`} onDragOver={(event) => event.preventDefault()} onDrop={(event) => { event.preventDefault(); chooseFile(event.dataTransfer.files[0] ?? null); }}>
        <input ref={inputRef} type="file" tabIndex={-1} aria-hidden="true" accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(event) => chooseFile(event.target.files?.[0] ?? null)} />
        {file ? <><FileSpreadsheet size={26} /><div><strong>{file.name}</strong><small>{(file.size / 1024).toFixed(1)} KB · {file.name.toLowerCase().endsWith(".csv") ? "在线 Listing CSV 自动识别" : "系统 XLSX 模板"}</small></div><button className="text-button" onClick={() => inputRef.current?.click()}>更换商品清单</button></> : <><UploadCloud size={28} /><div><strong>拖入商品清单 CSV 或 XLSX</strong><small>CSV 自动识别站点与商品字段 · 最大 {data?.settings.maxFileMb ?? 20} MB · 最多 {(data?.settings.maxRows ?? 10000).toLocaleString()} 行</small></div><button className="button secondary" onClick={() => inputRef.current?.click()}>上传商品清单</button></>}
      </div>
      <div className="task-form-actions">
        <button className="button primary" disabled={!data?.writeEnabled || !!busy || !file || !selectedOptions.length || Boolean(importJobId)} onClick={() => void runPrecheck("RUN")}>{busy === "precheck" || importJobId ? <LoaderCircle className="spin" size={17} /> : <CheckCircle2 size={17} />}{importJobId ? `后台解析 ${importJob?.progress ?? 0}%` : "上传并执行"}</button>
        <button className="button secondary" disabled={!!busy || !file || !selectedOptions.length || Boolean(importJobId)} onClick={() => void runPrecheck("PREVIEW")}>仅查看清单</button>
        <span className="muted">标题、描述和价格直接提交；空字段保持原值，单条失败继续处理其他记录。</span>
      </div>
      {error ? <div className="form-error" role="alert"><AlertTriangle size={17} />{error}</div> : null}
    </section>

    {createdTasks.length ? <section className="workspace-band"><div className="section-heading"><h2>已进入后台执行</h2><span>关闭页面后继续运行，成功以官方回读为准。</span></div>{createdTasks.map(task => <button className="button secondary" key={task.id} onClick={() => navigate(`/tasks/${task.id}`)}>{task.taskNo} · 查看结果 / 下载</button>)}</section> : null}
    {selectedTypes.includes("NET_PROCEEDS_UPDATE") ? <><OneTimePricingPanel /><details className="workspace-band"><summary>辅助费用核对工具</summary><p className="muted">用于费用核对，不会自动修改商品。规则计价请使用上方规则入口。</p><OfficialPricingPanel disabled={Boolean(importJobId) || Boolean(busy)} onUseFile={(nextFile) => { setSelectedTypes(["NET_PROCEEDS_UPDATE"]); chooseFile(nextFile); }} /><ShippingPricingPanel /></details></> : null}

    {activePrecheck ? <section className="workspace-band precheck-results">
      {prechecks.length > 1 ? <div className="precheck-type-tabs" role="tablist" aria-label="按功能查看预检结果">
        {prechecks.map((entry) => <button role="tab" aria-selected={entry.taskType === activePrecheck.taskType} className={entry.taskType === activePrecheck.taskType ? "active" : ""} key={entry.id} onClick={() => { setActivePrecheckType(entry.taskType); setFilter("ALL"); setPage(1); }}>
          <TaskTypeIcon type={entry.taskType} />
          <span><strong>{typeLabels[entry.taskType]}</strong><small>{entry.summary.executable} 条可执行</small></span>
        </button>)}
      </div> : null}
      <div className="section-heading"><div><h2>文件清单 · {typeLabels[activePrecheck.taskType]}</h2><span className="mono">SHA-256 {activePrecheck.fileHash.slice(0, 12)}…</span></div><StatusBadge status={activePrecheck.highRisk ? "WAITING_APPROVAL" : "READY"} /></div>
      <MetricStrip items={[
        { label: "总行数", value: activePrecheck.summary.total },
        { label: "可执行", value: activePrecheck.summary.executable, tone: "positive" },
        { label: "已拦截", value: activePrecheck.summary.blocked, tone: activePrecheck.summary.blocked ? "negative" : "" },
        { label: "无变化", value: activePrecheck.summary.skipped },
        { label: "预计批次", value: Math.ceil(activePrecheck.summary.executable / (data?.settings.batchSize ?? 200)), note: `每批 ${data?.settings.batchSize ?? 200} 条` },
      ]} />
      {activePrecheck.riskReasons.length ? <div className="risk-banner"><AlertTriangle size={20} /><div><strong>需要复核审批</strong>{activePrecheck.riskReasons.map((reason) => <span key={reason}>{reason}</span>)}</div></div> : null}
      <div className="result-toolbar">
        <div className="segmented compact">
          {["ALL", "READY", "BLOCKED", "SKIPPED_NO_CHANGE"].map((value) => <button key={value} className={filter === value ? "active" : ""} onClick={() => { setFilter(value); setPage(1); }}>{value === "ALL" ? "全部" : value === "READY" ? "可执行" : value === "BLOCKED" ? "已拦截" : "无变化"}</button>)}
        </div>
        <span>{filtered.length} 条</span>
      </div>
      <div className="table-scroll"><table className="data-table">
        <thead><tr><th>行</th><th>账号 / 站点</th><th>Item ID</th><th>旧值</th><th>目标值</th><th>状态</th><th>原因</th></tr></thead>
        <tbody>{visible.map((detail) => <tr key={detail.id}>
          <td className="mono">{detail.rowNo}</td><td><strong>{detail.accountCode}</strong><small>{detail.siteId}</small></td><td className="mono">{detail.itemId}</td><td className="value-cell">{readValue(detail.beforeSnapshot)}</td><td className="value-cell">{readValue(detail.targetPayload)}</td><td><StatusBadge status={detail.status} /></td><td>{detail.issues.map((entry) => entry.message).join("；") || "--"}</td>
        </tr>)}</tbody>
      </table></div>
      <Pagination page={page} total={filtered.length} pageSize={pageSize} onChange={setPage} />
      <div className="submit-bar"><div><strong>{data?.writeEnabled ? `${totalExecutable} 条变更将创建 ${submittablePrechecks.length} 个任务` : "预检完成，生产写入保持锁定"}</strong><span>{totalBlocked} 条未通过预检；没有可执行记录的功能不会创建任务</span></div><button className="button primary" disabled={!data?.writeEnabled || busy === "submit" || submittablePrechecks.length === 0} onClick={() => void submit()}>{busy === "submit" ? <LoaderCircle className="spin" size={17} /> : null}{data?.writeEnabled ? (submittablePrechecks.length > 1 ? `确认并创建 ${submittablePrechecks.length} 个任务` : submittablePrechecks[0]?.highRisk ? "提交审批" : "确认并执行") : "生产写入已锁定"}</button></div>
    </section> : null}
  </>;
}
